import { useRef, useCallback, useEffect } from 'react';

/**
 * Vehicle Path Sync Hook
 * Responsible for throttling path updates and synchronizing with external path state
 */
export const useVehiclePathSync = ({
  setPath,
  path,
  currentModeRef,
  isCustomMode
}) => {
  // Use refs to track external path and update status
  const pathRef = useRef(path);
  const isUpdatingRef = useRef(false);
  
  // Update ref value when path changes
  useEffect(() => {
    pathRef.current = path;
  }, [path]);
  
  // Throttled function to update external path - optimized with useCallback
  const updatePathThrottled = useCallback((newPaths) => {
    // Add safety check
    if (!newPaths || !Array.isArray(newPaths)) {
      console.error('[DEBUG-SYNC] Invalid newPaths:', newPaths);
      return;
    }
    
    try {
      // Merge all vehicle paths into a flat array
      const allPathIds = newPaths.flatMap(vehicle => 
        vehicle && vehicle.path ? 
          vehicle.path.map(point => point ? point.id : null).filter(Boolean) : 
          []
      );
      
      // Check if updating or path hasn't changed
      if (isUpdatingRef.current) {
        return;
      }
      
      // Check if paths are identical
      const pathsEqual = Array.isArray(pathRef.current) && 
                        pathRef.current.length === allPathIds.length && 
                        pathRef.current.every((id, i) => id === allPathIds[i]);
      
      if (pathsEqual && allPathIds.length > 0) {
        return;
      }
      
      // Create shallow copies to avoid reference issues (faster than deep copy)
      const newPathsCopy = newPaths.map(vehicle => ({
        ...vehicle,
        path: vehicle.path ? [...vehicle.path] : []
      }));
      
      const onlyStartPoints = newPathsCopy.every(vehicle => !vehicle.path || vehicle.path.length <= 1);
      
      // Update path references
      pathRef.current = allPathIds;
      
      // Simplified path update logic - no delays to prevent flickering
      if (onlyStartPoints) {
        // Clear paths but keep vehicle path info
        setPath([]);
        // Note: Session clearing optimized - will be handled during confirmation
      } else if (allPathIds.length > 1) {
        // Direct update without delay
        setPath([...allPathIds]);
      }
      
      // Shorter timeout to allow faster subsequent updates
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 100);
    } catch (err) {
      console.error('[DEBUG-SYNC] Error in updatePathThrottled:', err);
      isUpdatingRef.current = false;
    }
  }, [setPath, currentModeRef, isCustomMode]);
  
  // Cleanup callback on unmount
  useEffect(() => {
    return () => {
      isUpdatingRef.current = false;
    };
  }, []);
  
  return {
    updatePathThrottled,
    pathRef,
    isUpdatingRef
  };
};